import { isDefined } from "@clipboard-health/util-ts";
import { differenceInDays, startOfDay } from "date-fns";

import { getLocalTimeForPicker } from "./timezoneConversion";

interface GetDayIndicatorParams {
  selectedTimestamp: Date | undefined;
  /**
   * The timestamp to compare against (e.g., shift start for clock-in, shift end for clock-out)
   */
  referenceTimestamp: Date;
  workplaceTimezone: string;
}

/**
 * Returns a day indicator string when the selected timestamp falls on a different
 * calendar day than the reference timestamp.
 *
 * @returns "Next day" if selected timestamp is one day after reference,
 *          "Previous day" if one day before, or undefined if same day.
 */
export function getDayIndicator(params: GetDayIndicatorParams): string | undefined {
  const { selectedTimestamp, referenceTimestamp, workplaceTimezone } = params;

  if (!isDefined(selectedTimestamp)) {
    return undefined;
  }

  // Convert to local timezone for day comparison
  const selectedLocal = getLocalTimeForPicker(selectedTimestamp, workplaceTimezone);
  const referenceLocal = getLocalTimeForPicker(referenceTimestamp, workplaceTimezone);

  const dayDiff = differenceInDays(startOfDay(selectedLocal), startOfDay(referenceLocal));

  if (dayDiff === 1) {
    return "Next day";
  }

  if (dayDiff === -1) {
    return "Previous day";
  }

  return undefined;
}
